import { reactive } from 'vue';
import { rtcProvider } from './rtc';

const idleState = () => ({
  phase: 'idle',
  direction: '',
  mediaType: 'audio',
  peerUserID: '',
  peerName: '',
  peerPortrait: '',
  invitation: null,
  roomID: '',
  muted: false,
  cameraEnabled: false,
  cameraFacingMode: 'user',
  remoteVideoAvailable: false,
  videoTrackVersion: 0,
  startedAt: 0,
  error: '',
  result: ''
});

function callErrorMessage(error, fallback) {
  let message = '';
  if (typeof error === 'string' || typeof error === 'number') {
    message = String(error);
  } else if (error && typeof error === 'object') {
    message = String(error.message || error.errMsg || error.errDlt || '').trim();
    if (!message) {
      // The OpenIM WASM SDK spreads rejected strings onto its response object,
      // leaving their characters under numeric keys instead of `message`.
      const characterKeys = Object.keys(error)
        .filter(key => /^\d+$/.test(key))
        .sort((left, right) => Number(left) - Number(right));
      if (characterKeys.length) {
        message = characterKeys.map(key => error[key]).join('');
      }
    }
  }
  message = message
    .replace(/\bBearer\s+[^\s,;]+/gi, 'Bearer [redacted]')
    .replace(/((?:token|secret|password)\s*[:=]\s*)[^\s,;]+/gi, '$1[redacted]')
    .trim()
    .slice(0, 240);
  return message || fallback;
}

export const voiceCallState = reactive(idleState());

class VoiceCallService {
  constructor() {
    this.mediaConnection = null;
    this.generation = 0;
    this.callTimer = 0;
    rtcProvider.subscribeSignals(event => this.handleProviderSignal(event));
  }

  clearCallTimer() {
    if (this.callTimer) window.clearTimeout(this.callTimer);
    this.callTimer = 0;
  }

  async start(peerUserID, peerName, peerPortrait = '', mediaType = 'audio') {
    if (voiceCallState.phase !== 'idle') {
      throw new Error('当前已有通话');
    }
    const generation = ++this.generation;
    Object.assign(voiceCallState, {
      ...idleState(),
      phase: 'outgoing',
      direction: 'outgoing',
      mediaType: mediaType === 'video' ? 'video' : 'audio',
      peerUserID: String(peerUserID || ''),
      peerName: String(peerName || peerUserID || ''),
      peerPortrait: String(peerPortrait || '')
    });
    let invitation = null;
    const callLabel = voiceCallState.mediaType === 'video' ? '视频通话' : '语音通话';
    let failureStage = `${callLabel}邀请信令`;
    try {
      const result = await rtcProvider.invite(peerUserID, voiceCallState.mediaType);
      invitation = result.invitation;
      const { credentials } = result;
      if (generation !== this.generation) {
        await rtcProvider.cancel(invitation).catch(() => {});
        return;
      }
      voiceCallState.invitation = invitation;
      voiceCallState.roomID = invitation.roomID;
      this.clearCallTimer();
      this.callTimer = window.setTimeout(() => {
        if (generation !== this.generation || voiceCallState.roomID !== invitation.roomID) return;
        this.generation += 1;
        this.clearCallTimer();
        rtcProvider.cancel(invitation).catch(() => {});
        this.disconnectMedia();
        voiceCallState.result = 'timeout';
        voiceCallState.phase = 'ended';
      }, Math.max(5, Number(invitation.timeout) || 45) * 1000);
      failureStage = 'Agora 媒体连接';
      await this.connectMedia(credentials, false, generation);
    } catch (error) {
      if (generation !== this.generation) return;
      this.clearCallTimer();
      if (invitation) await rtcProvider.cancel(invitation).catch(() => {});
      await this.disconnectMedia();
      voiceCallState.phase = 'error';
      voiceCallState.error = callErrorMessage(error, `${callLabel}启动失败（${failureStage}）`);
    }
  }

  async accept() {
    if (voiceCallState.phase !== 'incoming' || !voiceCallState.invitation) return;
    const generation = ++this.generation;
    const invitation = voiceCallState.invitation;
    this.clearCallTimer();
    voiceCallState.phase = 'connecting';
    voiceCallState.error = '';
    try {
      const credentials = await rtcProvider.accept(invitation);
      if (generation !== this.generation) return;
      voiceCallState.roomID = credentials.roomID || invitation.roomID;
      await this.connectMedia(credentials, true, generation);
    } catch (error) {
      if (generation !== this.generation) return;
      await rtcProvider.hangUp(invitation).catch(() => {});
      await this.disconnectMedia();
      voiceCallState.phase = 'error';
      voiceCallState.error = callErrorMessage(error, `接听${voiceCallState.mediaType === 'video' ? '视频' : '语音'}通话失败`);
    }
  }

  async decline() {
    this.generation += 1;
    const invitation = voiceCallState.invitation;
    this.clearCallTimer();
    if (!invitation) return this.close();
    voiceCallState.phase = 'ending';
    try {
      await rtcProvider.reject(invitation);
      voiceCallState.result = 'declined';
      voiceCallState.phase = 'ended';
    } catch (error) {
      voiceCallState.phase = 'error';
      voiceCallState.error = callErrorMessage(error, '拒绝语音通话失败');
    }
  }

  async end() {
    this.generation += 1;
    const invitation = voiceCallState.invitation;
    const previousPhase = voiceCallState.phase;
    this.clearCallTimer();
    if (!invitation) {
      voiceCallState.result = 'cancelled';
      voiceCallState.phase = 'ended';
      return;
    }
    voiceCallState.phase = 'ending';
    try {
      if (voiceCallState.direction === 'outgoing' && previousPhase === 'outgoing') {
        await rtcProvider.cancel(invitation);
      } else {
        await rtcProvider.hangUp(invitation);
      }
      voiceCallState.result = 'ended';
      voiceCallState.phase = 'ended';
    } catch (error) {
      voiceCallState.phase = 'error';
      voiceCallState.error = callErrorMessage(error, '结束语音通话失败');
    } finally {
      await this.disconnectMedia();
    }
  }

  async setMuted(muted) {
    if (!this.mediaConnection) return;
    try {
      await this.mediaConnection.setMuted(Boolean(muted));
      voiceCallState.muted = Boolean(muted);
    } catch (error) {
      voiceCallState.error = callErrorMessage(error, '麦克风状态切换失败');
    }
  }

  async setCameraEnabled(enabled) {
    if (!this.mediaConnection || voiceCallState.mediaType !== 'video') return;
    try {
      voiceCallState.cameraEnabled = await this.mediaConnection.setCameraEnabled(Boolean(enabled));
    } catch (error) {
      voiceCallState.error = callErrorMessage(error, '摄像头状态切换失败');
    }
  }

  async switchCamera() {
    if (!this.mediaConnection || voiceCallState.mediaType !== 'video' || !voiceCallState.cameraEnabled) return;
    try {
      voiceCallState.cameraFacingMode = await this.mediaConnection.switchCamera();
      voiceCallState.videoTrackVersion += 1;
    } catch (error) {
      voiceCallState.error = callErrorMessage(error, '切换摄像头失败');
    }
  }

  attachVideoElements(localElement, remoteElement) {
    this.mediaConnection?.attachVideoElements?.(localElement, remoteElement);
  }

  close() {
    this.generation += 1;
    this.clearCallTimer();
    this.disconnectMedia();
    Object.assign(voiceCallState, idleState());
  }

  async connectMedia(credentials, accepting, generation = this.generation) {
    if (!window.isSecureContext && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
      throw new Error('通话需要通过 HTTPS 打开 EzLife');
    }
    const connection = await rtcProvider.connect({
      credentials,
      mediaType: voiceCallState.mediaType,
      onError: error => {
        if (generation !== this.generation) return;
        voiceCallState.error = callErrorMessage(error, 'Agora 媒体连接发生错误');
      },
      onParticipantConnected: () => {
        if (generation !== this.generation || ['ending', 'ended', 'idle'].includes(voiceCallState.phase)) return;
        this.clearCallTimer();
        voiceCallState.phase = 'active';
        if (!voiceCallState.startedAt) voiceCallState.startedAt = Date.now();
      },
      onDisconnected: () => {
        if (generation !== this.generation) return;
        this.mediaConnection = null;
        voiceCallState.cameraEnabled = false;
        voiceCallState.remoteVideoAvailable = false;
        voiceCallState.videoTrackVersion += 1;
        if (voiceCallState.phase !== 'ending' && voiceCallState.phase !== 'ended') {
          voiceCallState.phase = 'error';
          voiceCallState.error = `${voiceCallState.mediaType === 'video' ? '视频' : '语音'}媒体连接已断开`;
        }
      },
      onVideoTracksChanged: update => {
        if (generation !== this.generation) return;
        voiceCallState.videoTrackVersion += 1;
        if (typeof update?.remoteVideoAvailable === 'boolean') {
          voiceCallState.remoteVideoAvailable = update.remoteVideoAvailable;
        }
      }
    });
    if (generation !== this.generation || ['ending', 'ended', 'error', 'idle'].includes(voiceCallState.phase)) {
      await connection.disconnect().catch(() => {});
      return;
    }
    this.mediaConnection = connection;
    voiceCallState.cameraEnabled = voiceCallState.mediaType === 'video';
    voiceCallState.cameraFacingMode = connection.cameraFacingMode || 'user';
    if (accepting && voiceCallState.phase !== 'active') {
      voiceCallState.phase = 'connecting';
    }
  }

  async disconnectMedia() {
    const connection = this.mediaConnection;
    this.mediaConnection = null;
    await connection?.disconnect().catch(() => {});
    voiceCallState.cameraEnabled = false;
    voiceCallState.cameraFacingMode = 'user';
    voiceCallState.remoteVideoAvailable = false;
    voiceCallState.videoTrackVersion += 1;
  }

  handleProviderSignal(event) {
    const roomID = String(event?.roomID || event?.invitation?.roomID || '');
    if (event?.type === 'incoming') {
      if (voiceCallState.phase !== 'idle') {
        rtcProvider.reject(event.invitation).catch(() => {});
        return;
      }
      const mediaType = event.invitation?.mediaType === 'video' ? 'video' : 'audio';
      Object.assign(voiceCallState, {
        ...idleState(),
        phase: 'incoming',
        direction: 'incoming',
        mediaType,
        peerUserID: String(event.peer?.userID || event.invitation?.inviterUserID || ''),
        peerName: String(event.peer?.name || event.invitation?.inviterUserID || ''),
        peerPortrait: String(event.peer?.portrait || ''),
        invitation: event.invitation,
        roomID
      });
      return;
    }
    if (!roomID || roomID !== voiceCallState.roomID) return;

    if (event.type === 'accepted') {
      this.clearCallTimer();
      if (voiceCallState.phase === 'outgoing') voiceCallState.phase = 'connecting';
    } else if (event.type === 'rejected') {
      this.clearCallTimer();
      voiceCallState.result = 'declined';
      this.finishFromRemote();
    } else if (event.type === 'cancelled') {
      this.clearCallTimer();
      voiceCallState.result = 'cancelled';
      this.finishFromRemote();
    } else if (event.type === 'timeout') {
      this.clearCallTimer();
      voiceCallState.result = 'timeout';
      this.finishFromRemote();
    } else if (event.type === 'answeredElsewhere') {
      this.clearCallTimer();
      voiceCallState.result = 'answeredElsewhere';
      this.finishFromRemote();
    } else if (event.type === 'hangup') {
      this.clearCallTimer();
      voiceCallState.result = 'ended';
      this.finishFromRemote();
    }
  }

  finishFromRemote() {
    this.generation += 1;
    this.clearCallTimer();
    this.disconnectMedia();
    if (voiceCallState.phase !== 'error') voiceCallState.phase = 'ended';
  }
}

export const voiceCallService = new VoiceCallService();
